import { hexToRGB } from '@repo-level/core-front';
import {
  device,
  doublePadding,
  ScreenSize,
} from 'components/styles/MediaQuery';
import styled, { css } from 'styled-components/macro';

export interface ScButtonCrossProps {
  relative?: boolean;

  left?: number | string;
  right?: number;
  top?: number;
  bottom?: number;

  inCircle?: boolean;
  small?: boolean;
  smallWithBg?: boolean;

  square?: boolean;
  gray?: boolean;
  transparent?: boolean;
}

export const ScButtonCross = styled.button<ScButtonCrossProps>`
  position: ${({ relative }) => (relative ? 'relative' : 'absolute')};
  left: ${({ left }) => (left != null ? `${left}px` : 'auto')};
  right: ${({ right }) => (right != null ? `${right}px` : 'auto')};
  top: ${({ top }) => (top != null ? `${top}px` : 'auto')};
  bottom: ${({ bottom }) => (bottom != null ? `${bottom}px` : 'auto')};

  display: flex;
  justify-content: center;
  align-items: center;

  background: ${({ square, transparent }) => {
    if (square && transparent) return 'rgba(255, 255, 255, 0.2)';
    if (square) return '#1F202C';
    return 'none';
  }};
  border: none;
  border-radius: ${({ small }) => (small ? '8px' : '12px')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: 0.3s;

  svg {
    width: ${({ inCircle, small }) => (inCircle || small ? '24px' : '40px')};
    height: ${({ inCircle, small }) => (inCircle || small ? '24px' : '40px')};

    object-fit: contain;
    transition: 0.3s;
    path {
      fill: ${({ theme, gray }) =>
        gray ? theme.grayColor1 : hexToRGB(theme.whiteColor, 0.4)};
    }
  }

  @media (min-width: 640px) {
    &:hover {
      svg {
        transform: scale(1.05);

        path {
          fill: ${({ theme, gray }) =>
            gray ? theme.blackColor : theme.whiteColor};
        }

        circle {
          stroke: ${({ theme }) => hexToRGB(theme.whiteColor, 1)};
        }
      }
    }
  }

  ${({ small, smallWithBg }) => {
    switch (true) {
      case smallWithBg != null:
        return css`
          width: 32px;
          height: 32px;
        `;
      case small != null:
        return css`
          width: 24px;
          height: 24px;
        `;
      default:
        return css`
          width: 40px;
          height: 40px;
        `;
    }
  }}
`;

export interface ScButtonMoreProps {
  top?: number;
  right?: number;
  active?: boolean;
  vertical?: boolean;
}

export const ScButtonMore = styled.button<ScButtonMoreProps>`
  position: absolute;
  top: ${({ top }) => `${top || 0}px`};
  right: ${({ right }) => `${right || 0}px`};
  width: 32px;
  height: 40px;
  display: block;
  background-color: transparent;
  border: none;
  transform: ${({ active, vertical }) =>
    `rotate(${active && !vertical ? 0 : 90}deg)`};
  transition: 0.3s;
  cursor: pointer;
  z-index: 1;

  @media (min-width: ${ScreenSize.MOBILE + doublePadding}px) {
    &:hover {
      transform: ${({ active, vertical }) =>
        `rotate(${active && !vertical ? 90 : 0}deg)`};
    }
  }

  & > div {
    border-radius: 50%;
    display: inline-block;
    vertical-align: middle;
    width: 4px;
    height: 4px;
    background: ${({ theme }) => theme.grayColor1};
    margin: 0 2px;
  }
`;

export interface ScSearchButtonProps {
  alwaysWide?: boolean;
  isABVersion?: boolean;
}

export const ScSearchButton = styled.div<ScSearchButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  max-width: ${({ alwaysWide }) => (alwaysWide ? '100%' : '344px')};

  & > a {
    width: 100%;

    & > button {
      width: 100%;

      .btn-content {
        display: grid;
        grid-template-columns: 16px 1fr;

        & path {
          fill: ${({ theme }) => theme.grayColor1};
        }
      }
    }
  }

  @media (${device.mobileS}) {
    max-width: 100%;
  }

  @media (max-width: 344px) {
    & > a > button {
      padding: 0 12px;

      .btn-content {
        gap: 8px;
      }
    }
  }

  .content {
    position: relative;
    width: 100%;
    height: 20px;
    text-transform: capitalize;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    color: ${({ theme }) => theme.grayColor1};

    @media (max-width: 344px) {
      font-size: 12px;
      line-height: 18px;
    }

    ${({ isABVersion }) =>
      isABVersion &&
      css`
        text-transform: capitalize;
        text-align: left;
      `}

    .step-1,
    .step-2 {
      text-transform: capitalize;
    }

    .step-3 {
      text-transform: initial;
    }

    .step-1,
    .step-2,
    .step-3 {
      position: absolute;
      left: 0;
      opacity: 0;
      z-index: 2;
      animation: phrase-change 6s linear infinite;
      will-change: animation;
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;

      @media (max-width: 344px) {
        font-size: 12px;
        line-height: 18px;
      }

      .word {
        opacity: 0;
        animation: word-appear 6s linear infinite;
        will-change: animation;
      }

      .word:nth-child(1) {
        animation-delay: 0s;
      }

      .word:nth-child(2) {
        animation-delay: 0.35s;
      }

      .word:nth-child(3) {
        animation-delay: 0.7s;
      }

      .word:nth-child(4) {
        animation-delay: 1.05s;
      }

      .word:nth-child(5) {
        animation-delay: 1.4s;
      }

      .word:nth-child(6) {
        animation-delay: 1.75s;
      }
    }

    .step-2 {
      animation-delay: 2.5s;
    }

    .step-3 {
      animation-delay: 3.25s;

      .word:nth-child(1) {
        animation-delay: 3.75s;
      }

      .word:nth-child(2) {
        animation-delay: 4s;
      }

      .word:nth-child(3) {
        animation-delay: 4.25s;
      }

      .word:nth-child(4) {
        animation-delay: 4.5s;
      }

      .word:nth-child(5) {
        animation-delay: 4.75s;
      }

      .word:nth-child(6) {
        animation-delay: 5s;
      }
    }

    @keyframes word-appear {
      0% {
        opacity: 0;
      }

      5% {
        opacity: 1;
      }

      60% {
        opacity: 1;
      }

      61% {
        opacity: 0;
      }

      100% {
        opacity: 0;
      }
    }

    @keyframes phrase-change {
      0% {
        opacity: 0;
      }

      5% {
        opacity: 1;
      }

      35% {
        opacity: 1;
      }

      40% {
        opacity: 0;
      }

      100% {
        opacity: 0;
      }
    }
  }
`;

export const ScFilterButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  max-width: 220px;

  @media (${device.mobileS}) {
    max-width: 48px;
  }

  & > a {
    width: 100%;

    & > button {
      width: 100%;

      @media (${device.mobileS}) {
        padding: 0 14px;
      }

      .btn-content {
        display: grid;
        grid-template-columns: 20px 1fr;

        @media (${device.mobileS}) {
          grid-template-columns: 20px;
        }
      }
    }
  }

  .content {
    width: 100%;
    text-transform: capitalize;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-align: left;

    @media (max-width: 344px) {
      font-size: 12px;
      line-height: 18px;
    }
  }
`;
